/**
 * Step type data for cadence step pickers.
 * Pure data that can be used in any platform.
 */
import type { ReactNode } from 'react';

import type { StepIconSvgType } from '../StepIcon';
import { StepIcon } from '../StepIcon';

export type CadenceStepItem<T extends StepIconSvgType = StepIconSvgType> = {
  label: string;
  labelFull: string;
  labelFullIfResponse: string;
  value: T;
  icon: ReactNode;
  error?: string;
};

export const messageStep: CadenceStepItem<'message'> = {
  label: 'Message',
  labelFull: 'Send Linkedin message',
  labelFullIfResponse: 'Respond with Linkedin message',
  value: 'message',
  icon: <StepIcon iconType="message" />,
  error: 'You are not connected to this contact.',
};

export const inmailStep: CadenceStepItem<'inmail'> = {
  label: 'InMail',
  labelFull: 'Send Linkedin Inmail',
  labelFullIfResponse: 'Respond with Linkedin message',
  value: 'inmail',
  icon: <StepIcon iconType="inmail" />,
  error: 'You are already connected.',
};

export const callStep: CadenceStepItem<'call'> = {
  label: 'Call',
  labelFull: 'Call contact',
  labelFullIfResponse: 'Make a phone call',
  value: 'call',
  icon: <StepIcon iconType="call" />,
  error: 'There is no phone number assigned, enrich the contact to find one.',
};

export const voiceNoteStep: CadenceStepItem<'voice_note'> = {
  label: 'Voice Note',
  labelFull: 'Send a voice note',
  labelFullIfResponse: 'Send a voice note',
  value: 'voice_note',
  icon: <StepIcon iconType="voice_note" />,
  error: 'You can not send voice drops',
};

export const connectStep: CadenceStepItem<'connect'> = {
  label: 'Connect',
  labelFull: 'Send Linkedin Connection',
  labelFullIfResponse: 'Send Linkedin Connection',
  value: 'connect',
  icon: <StepIcon iconType="connect" />,
  error: 'You are already connected.',
};

export const switchCampaignStep: CadenceStepItem<'switch_campaign'> = {
  label: 'Change to campaign',
  labelFull: 'Change to another campaign',
  labelFullIfResponse: 'Change to another campaign',
  value: 'switch_campaign',
  icon: <StepIcon iconType="switch_campaign" />,
};

export const switchStep: CadenceStepItem<'switch'> = {
  label: 'Switch',
  labelFull: 'Branch by a numeric attribute',
  labelFullIfResponse: 'Branch by a numeric attribute',
  value: 'switch',
  icon: <StepIcon iconType="switch" />,
};

export const visitStep: CadenceStepItem<'visit'> = {
  label: 'Visit',
  labelFull: 'Visit Linkedin profile',
  labelFullIfResponse: 'Visit Linkedin profile',
  value: 'visit',
  icon: <StepIcon iconType="visit" />,
};

export const emailStep: CadenceStepItem<'email'> = {
  label: 'Email',
  labelFull: 'Send Email',
  labelFullIfResponse: 'Respond with Email',
  value: 'email',
  icon: <StepIcon iconType="email" />,
  error: 'There is no email address assigned, enrich the contact to find one.',
};

export const reminderStep: CadenceStepItem<'reminder'> = {
  label: 'Task',
  labelFull: 'Get reminded',
  labelFullIfResponse: 'Get reminded',
  value: 'reminder',
  icon: <StepIcon iconType="reminder" />,
};

export const likeRecentPostsStep: CadenceStepItem<'likeRecentPosts'> = {
  label: 'Like',
  labelFull: 'Like recent posts',
  labelFullIfResponse: 'Like recent posts',
  value: 'likeRecentPosts',
  icon: <StepIcon iconType="likeRecentPosts" />,
};

export const followStep: CadenceStepItem<'follow'> = {
  label: 'Follow',
  labelFull: 'Follow Linkedin',
  labelFullIfResponse: 'Follow Linkedin',
  value: 'follow',
  icon: <StepIcon iconType="follow" />,
};

export const siftStep: CadenceStepItem<'sift'> = {
  label: 'Sift',
  labelFull: 'Filter contacts',
  labelFullIfResponse: 'Filter contacts',
  value: 'sift',
  icon: <StepIcon iconType="sift" />,
};

/** Steps that can be selected when adding/changing a step type */
export const selectableCadenceStepsList: CadenceStepItem[] = [
  connectStep,
  messageStep,
  inmailStep,
  likeRecentPostsStep,
  followStep,
  visitStep,
  emailStep,
  reminderStep,
  voiceNoteStep,
];

/** All cadence step types */
export const cadenceStepsList: CadenceStepItem[] = [
  connectStep,
  messageStep,
  inmailStep,
  visitStep,
  emailStep,
  reminderStep,
  callStep,
  likeRecentPostsStep,
  siftStep,
  voiceNoteStep,
];

/** Steps that can trigger responses */
export const triggerSteps: CadenceStepItem[] = [messageStep, inmailStep, visitStep, emailStep, reminderStep, callStep];

/** LinkedIn-specific steps */
export const linkedinStepsList: CadenceStepItem[] = [connectStep, messageStep, inmailStep];

/** Email steps */
export const emailStepsList: CadenceStepItem[] = [emailStep];
